import { PaytrieAPI } from '@paytrie/client'

import { sleep } from '../utils/sleep'
import { TOKEN_POLL_INTERVAL_MS, TOKEN_POLL_TIMEOUT_MS } from './constants'

export type PollForAccessTokenResult =
  | {
      status: 'verified'
      accessToken: string
      tokenType: PaytrieAPI.AuthTokenVerifiedTokenType
      expiresIn: number
    }
  | { status: 'denied' }
  | { status: 'timed-out' }

/**
 * Polls `POST /v2/auth/tokens` until the session reaches a terminal state,
 * the signal is aborted, or the deadline expires.
 *
 * Transient failures (non-200 responses, fetch rejections) are swallowed and
 * retried on the next interval. Callers that need visibility into those
 * failures should pass `onTransientError`.
 *
 * Rejection behaviour on abort is asymmetric: if the signal fires during
 * `throwIfAborted` or `sleep`, the function rejects with an `AbortError`;
 * if it fires mid-fetch, the function rejects with whatever the fetch threw
 * (so the underlying error context is preserved). Callers should branch on
 * `signal.aborted`, not on the error type.
 */
export const pollForAccessToken = async ({
  sessionId,
  intervalMs = TOKEN_POLL_INTERVAL_MS,
  timeoutMs = TOKEN_POLL_TIMEOUT_MS,
  signal,
  onTransientError,
}: {
  sessionId: string
  intervalMs?: number
  timeoutMs?: number
  signal?: AbortSignal
  onTransientError?: (err: unknown) => void
}): Promise<PollForAccessTokenResult> => {
  const deadline = Date.now() + timeoutMs

  while (Date.now() < deadline) {
    signal?.throwIfAborted()

    try {
      const response = await PaytrieAPI.pollAuthToken({
        sessionId,
      })

      if (response.status === 200) {
        const envelope = response.data
        if (
          envelope.data.status === PaytrieAPI.AuthTokenVerifiedStatus.verified
        ) {
          const { accessToken, tokenType, expiresIn } = envelope.data
          return { status: 'verified', accessToken, tokenType, expiresIn }
        }
        if (envelope.data.status === PaytrieAPI.AuthTokenDeniedStatus.denied) {
          return { status: 'denied' }
        }
      } else {
        onTransientError?.(
          new Error(`Token poll received status ${response.status}`)
        )
      }
    } catch (err) {
      if (signal?.aborted) throw err
      onTransientError?.(err)
    }

    await sleep(intervalMs, signal)
  }

  return { status: 'timed-out' }
}
